import React, { useState } from "react";
import { Modal, Select, Button } from "antd";
import TextArea from "antd/es/input/TextArea";
import Buttons from "../../components/form/Buttons";
import DefaultSelect from "../../components/form/DefaultSelect";
import "./OrderStatusChangeModal.css";
import { orderStatuses, userRoles } from "../../types/enums";
import { toast } from "react-toastify";

const { Option } = Select;

const statusOptions = [
	{ value: 5, label: "Në transport" },
	{ value: 14, label: "Ne Transport Internacional" },
];

interface OrderStatusModalProps {
	visible: boolean;
	onClose: () => void;
	selectedOrders: any;
	onSave: (selectedData: any) => void;
	allUsers: any;
	defaultSystemCountryId?: any;
}

const OrderStatusModal: React.FC<OrderStatusModalProps> = ({
	visible,
	onClose,
	selectedOrders,
	onSave,
	allUsers,
	defaultSystemCountryId,
}) => {
	const [status, setStatus] = useState<number | null>(null);
	const [userId, setUserId] = useState<number | null>(null);
	const [userOptions, setUserOptions] = useState<any[]>([]);

	const onStatusChange = (value: number) => {
		setStatus(value);
		setUserId(null);

		const roleMapping: { [key: number]: number[] } = {
			[orderStatuses.TRANSPORTING]: [
				userRoles.DRIVER,
				userRoles.SUPERDRIVER,
			],
			[orderStatuses.DELIVERED]: [
				userRoles.DRIVER,
				userRoles.SUPERDRIVER,
			],
			[orderStatuses.REJECTED]: [userRoles.DRIVER, userRoles.SUPERDRIVER],
			[orderStatuses.PENDING]: [userRoles.DRIVER, userRoles.SUPERDRIVER],
			[orderStatuses.PICKED_UP]: [
				userRoles.COLLECTOR,
				userRoles.SUPERDRIVER,
			],
			[orderStatuses.IN_STORAGE]: [
				userRoles.WAREHOUSEMANAGER,
				userRoles.WAREHOUSE,
			],
			[orderStatuses.INTERNATIONAL_TRANSPORTING]: [
				userRoles.INTERNATIONAL_DRIVER,
			],
		};

		const roles = roleMapping[value];

		if (roles) {
			const filteredUsers = allUsers
				.filter((user: any) => roles.includes(user.userRoleId))
				.map((user: any) => ({
					value: user.id,
					label: `${user.firstName} ${user.lastName} - ${user.userRoleDescription}`,
				}));
			setUserOptions(filteredUsers);
		} else {
			const users = allUsers
				.filter(
					(user: any) =>
						![
							userRoles.CLIENT,
							userRoles.SUBCLIENT,
							userRoles.CHATBOT,
						].includes(user.userRoleId)
				)
				.map((user: any) => ({
					value: user.id,
					label: `${user.firstName} ${user.lastName} - ${user.userRoleDescription}`,
				}));
			setUserOptions(users);
		}
	};

	const handleSaveStatus = (selectedData: any[]) => {
		// Loop through each selected order and validate
		for (let order of selectedData) {
			// Define the statuses not allowed for international and return orders
			const notAllowedStatusChangesForInternationalOrders = [
				orderStatuses.TRANSPORTING,
				orderStatuses.DELIVERED,
				orderStatuses.REJECTED,
				orderStatuses.PENDING,
				orderStatuses.AWAITING_WITHDRAWL,
				orderStatuses.RETURNED,
				orderStatuses.REDIRECTED,
				orderStatuses.RETURN_STORAGE,
			];
			const notAllowedStatusChangesForReturnOrders = [
				orderStatuses.AWAITING_WITHDRAWL,
				orderStatuses.REJECTED,
				orderStatuses.RETURNED,
				orderStatuses.RETURN_STORAGE,
				orderStatuses.INTERNATIONAL_TRANSPORTING,
				orderStatuses.REDIRECTED,
			];

			// Show error utility
			const showError = (message: string, orderId: number) => {
				toast.error(`${message} (Porosia Id: #${orderId})`);
				return false;
			};
			console.log(
				order.internationalStatusId,
				selectedData,
				"internationalStatusId"
			);
			// Validation logic for returnable orders
			if (order.returnable) {
				if (
					order.statusId !== null &&
					notAllowedStatusChangesForReturnOrders.includes(
						order.statusId
					)
				) {
					return showError(
						"Kjo porosi eshte kthim i parave, nuk mund te ia ndryshoni statusin!",
						order?.displayId
					);
				}

				if (order.statusId === orderStatuses.DELIVERED) {
					return showError(
						"Kjo porosi eshte kthim i pageses qe eshte dorezuar, nuk mund te ia ndryshoni statusin!",
						order.displayId
					);
				}
			}

			// Validation logic for international orders
			if (
				order.countryId !== parseInt(defaultSystemCountryId) &&
				order.statusId !== null &&
				notAllowedStatusChangesForInternationalOrders.includes(
					order.statusId
				)
			) {
				return showError(
					"Kjo porosi eshte internacionale dhe nuk mund te ia ndryshoni statusin!",
					order.displayId
				);
			}

			// Additional international order validations
			if (
				order.internationalStatusId !== null &&
				order.internationalStatusId !== 0 &&
				order.statusId === orderStatuses.REGISTERED
			) {
				if (
					order.internationalStatusId !==
					orderStatuses.INTERNATIONAL_TRANSPORTING
				) {
					return showError(
						`Kjo Porosi eshte internacionale dhe nuk eshte ende ${"Në Tranzit"} per tek ju!`,
						order.displayId
					);
				}

				if (
					order.statusId !== null &&
					order.internationalStatusId ===
						orderStatuses.INTERNATIONAL_TRANSPORTING &&
					order.statusId !== orderStatuses.IN_STORAGE
				) {
					return showError(
						"Porosia eshte internacionale dhe duhet te pranohet ne depo para se te vazhdoje me hapa te tjere!",
						order.displayId
					);
				}
			}

			// Check for the "Picked Up" status on international orders
			if (
				order.internationalStatusId !== null &&
				order.internationalStatusId !== 0 &&
				order.internationalStatusId ===
					orderStatuses.INTERNATIONAL_TRANSPORTING &&
				order.StatusId === orderStatuses.PICKED_UP
			) {
				return showError(
					"Kjo porosi eshte internacionale dhe nuk mund te grumbullohet!",
					order.displayId
				);
			}

			// Check if the order is completed or part of a settlement
			if (order.completed === 1 || order.settlementStatus > 0) {
				return showError(
					"Kjo porosi eshte e procesuar ne nje barazim, nuk mund te ia ndryshoni statusin!",
					order.displayId
				);
			}
			if (
				order.statusId !== orderStatuses.IN_STORAGE &&
				order.statusId !== orderStatuses.TRANSPORTING
			) {
				return showError(
					"Kjo porosi nuk eshte ne statuset, Ne depo apo Ne Transport",
					order.displayId
				);
			}

			if (!status || !userId) {
				toast.error("Ju lutem zgjedhni nje Status dhe User");
				return false;
			}
			// return false;
		}

		// If all orders pass validation, proceed with the save
		console.log("All selected orders are valid", selectedData);
		return true;
	};

	const handleSave = () => {
		const isValid = handleSaveStatus(selectedOrders);

		if (isValid) {
			const selectedData = selectedOrders.map((order: any) => ({
				OrderId: order.id,
				StatusId: status,
				UserIdAssign: userId,
				OrderPrice: order.orderPrice,
				TransportPrice: order.transportPrice,
				DriverPrice: order.driverPrice,
				UUID: order.uuid,
				OriginCountryId: order.originCountryId,
				CountryId: order.countryId,
			}));
			// If valid, proceed with saving
			console.log("Status change is valid for all selected orders.");
			onSave(selectedData); // Assuming this is where you send the status change to the backend
		} else {
			// Validation failed, so do not proceed
			console.log("Validation failed, cannot proceed with saving.");
		}
	};

	const handleClose = () => {
		setStatus(null);
		setUserId(null);

		onClose();
	};

	return (
		<Modal
			open={visible}
			onCancel={handleClose}
			title="Ndrysho Statusin e porosise!"
			footer={[
				<>
					<Buttons
						button1Text="Mbyll"
						onButton1Click={handleClose}
						button2Text="Ruaj"
						onButton2Click={handleSave}
					/>
				</>,
			]}
			width={1000}
		>
			<>
				<div style={{ display: "flex", gap: "20px" }}>
					{/* Left Column: Displaying selected Order IDs */}
					<div style={{ flex: 1 }}>
						<p>
							Ndryshoni Statusin per {selectedOrders.length}{" "}
							porosi.
						</p>
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: "8px",
							}}
						>
							{selectedOrders.map((order: any) => (
								<div
									key={order.OrderId}
									style={{
										padding: "5px 10px",
										border: "1px solid #d9d9d9",
										borderRadius: "4px",
										backgroundColor: "#f5f5f5",
										display: "inline-block",
									}}
								>
									<strong>#</strong>

									{order.displayId}
								</div>
							))}
						</div>
					</div>
					<div style={{ flex: 1 }} className=" gap-4">
						{/* <h3>Ndrysho Statusin</h3> */}

						<DefaultSelect
							label="- Zgjedhe statusin"
							placeholder="- Zgjedhe statusin"
							width={320}
							options={statusOptions}
							onChange={onStatusChange}
							value={status}
							className="input"
							flexDirection="col"
							alignCenter="start"
						/>
						<DefaultSelect
							// width={320}
							label="Zgjedh Pergjegjesin"
							placeholder="- Zgjedhe pergjegjesin"
							options={userOptions}
							onChange={setUserId}
							value={userId}
							className="input"
							flexDirection="col"
							alignCenter="start"
						/>
						{/* <br />
						<br />
						<TextArea
							placeholder="Add status change comment"
							onChange={(e) =>
								setStatusChangeComment(e.target.value)
							}
							rows={4}
						/> */}
					</div>
				</div>
			</>
		</Modal>
	);
};

export default OrderStatusModal;
